import { Check, Send } from "lucide-react";
import { toast } from "sonner";
import { BillStatus } from "@boo/shared";
import { Button } from "@/components/ui/button";
import { formatBaht, sharesFor } from "../lib/billMath";
import type { BillEditor } from "../types/bills.types";
import PersonAvatar from "./PersonAvatar";

const friendLink = (token: string, personId: string) =>
  `${window.location.origin}/b/${token}?p=${personId}`;

async function sendReminder(title: string, name: string, url: string) {
  const text = `Hi ${name}, please check your share of "${title}" and tap "My share is correct":`;
  if (typeof navigator.share === "function") {
    try {
      await navigator.share({ title, text, url });
    } catch {
      // share sheet dismissed
    }
    return;
  }
  await navigator.clipboard.writeText(`${text} ${url}`);
  toast.success("Reminder copied");
}

/**
 * Who has confirmed their share. Owner-side view of the friends' "correct"
 * buttons, with a one-tap reminder for anyone still pending.
 */
export default function ReviewStatus({ editor }: { editor: BillEditor }) {
  const open = editor.status === BillStatus.Open;
  const token = editor.share?.token;
  if (!open || !token || editor.dirty) return null;

  const owed = new Map(
    sharesFor(
      editor.people,
      editor.items,
      editor.charges,
      editor.goDutch,
    ).shares.map((s) => [s.personId, s.rounded]),
  );
  const friends = editor.people
    .filter((p) => !p.isOwner)
    .map((p) => ({
      person: p,
      amount: owed.get(p.id) ?? 0,
    }))
    .filter((f) => f.amount > 0);
  if (friends.length === 0) return null;

  const done = friends.filter((f) => editor.reviewed[f.person.id]).length;

  return (
    <section className="space-y-3 rounded-xl border border-border bg-card p-4">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-foreground">
          Friends&apos; review
        </h2>
        <span className="text-xs text-muted-foreground">
          {done}/{friends.length} confirmed
        </span>
      </div>

      {editor.readyToFinalize && (
        <div className="rounded-lg bg-primary/10 px-3 py-2 text-sm text-primary">
          Everyone confirmed their share — ready to finalize and accept payment.
        </div>
      )}

      <ul className="space-y-1.5">
        {friends.map(({ person, amount }) => {
          const reviewed = editor.reviewed[person.id];
          return (
            <li
              key={person.id}
              className="flex items-center justify-between gap-2 text-sm"
            >
              <span className="flex min-w-0 items-center gap-2 truncate text-foreground">
                <PersonAvatar person={person} size={24} />
                {person.name}{" "}
                <span className="text-muted-foreground [font-variant-numeric:tabular-nums]">
                  {formatBaht(amount)}
                </span>
              </span>
              {reviewed ? (
                <span className="inline-flex shrink-0 items-center gap-1 text-xs text-primary">
                  <Check className="w-3.5 h-3.5" /> Confirmed
                </span>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={async () => {
                    try {
                      await sendReminder(
                        editor.title,
                        person.name,
                        friendLink(token, person.id),
                      );
                    } catch {
                      toast.error("Could not share");
                    }
                  }}
                >
                  <Send className="w-3.5 h-3.5 mr-1.5" /> Remind
                </Button>
              )}
            </li>
          );
        })}
      </ul>
      {!editor.readyToFinalize && (
        <p className="text-[11px] text-muted-foreground">
          You can still finalize any time; this is just a heads-up.
        </p>
      )}
    </section>
  );
}
